import { useCallback, useEffect } from 'react';

import scrollbarSize, { getScrollParent } from '../utils/dom';

const removeBodyScrolling = (isFullScreen = false) => {
  const scrollingElement = getScrollParent();
  scrollingElement.style.overflowY = 'hidden';

  if (isFullScreen) {
    scrollingElement.style.marginRight = `${scrollbarSize(true)}px`;
    scrollingElement.style.scrollbarGutter = 'auto';
  }
};

const restoreBodyScrolling = () => {
  const dialogs = Array.from(document.querySelectorAll('dialog'));
  const openDialogs = dialogs.filter((dialog) => dialog.open).length;
  const scrollingElement = getScrollParent();
  const isFullScreenModalOpen = dialogs.some((dialog) => dialog.open && dialog.getAttribute('data-fullscreen') === 'true');

  if (!openDialogs) {
    scrollingElement.style.removeProperty('margin-right');
  }

  // these properties are only needed for fullscreen modals
  if (!isFullScreenModalOpen) {
    scrollingElement.style.removeProperty('overflow-y');
    scrollingElement.style.removeProperty('scrollbar-gutter');
  }
};

// shared variable between `useModal` usages
let originElement: HTMLElement | undefined;

export const useModal = (isFullScreen = false) => {
  const restoreFocus = useCallback(() => {
    if (!originElement) {
      return;
    }

    const dialogs = Array.from(document.querySelectorAll('dialog'));
    const openDialogs = dialogs.filter((dialog) => dialog.open).length;

    // this was the last open dialog
    if (openDialogs === 0) {
      // restore focus when the focus is "lost" to the body element
      if (document.activeElement === document.body) {
        originElement.focus();
      }

      // always clear the originElement, or it will be set to a wrong element for future modals
      originElement = undefined;
    }
  }, []);

  useEffect(() => {
    return () => {
      restoreBodyScrolling();
      restoreFocus();
    };
  }, [restoreFocus]);

  const handleOpen = useCallback(() => {
    if (!originElement) {
      originElement = document.activeElement as HTMLElement;
    }

    removeBodyScrolling(isFullScreen);
  }, [isFullScreen]);

  const handleClose = useCallback(() => {
    restoreFocus();
    restoreBodyScrolling();
  }, [restoreFocus]);

  return { handleOpen, handleClose };
};
